import React from 'react';
import { HelpCircle, ChevronUp, Globe } from 'lucide-react';
import { InfoTooltip } from './InfoTooltip';

interface WebhookConfigProps {
  webhookUrl: string;
  showInfo: boolean;
  onWebhookChange: (url: string) => void;
  onToggleInfo: () => void;
}

export const WebhookConfig: React.FC<WebhookConfigProps> = ({
  webhookUrl,
  showInfo,
  onWebhookChange,
  onToggleInfo,
}) => {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
          <span>Custom Webhook URL (Optional)</span>
          <InfoTooltip text="Add a webhook URL to receive real-time notifications when your post is published. Useful for automation and integrations with external services." />
        </label>
        <button
          onClick={onToggleInfo}
          className="p-1 hover:bg-gray-100 rounded-full text-gray-500"
        >
          {showInfo ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <HelpCircle className="w-5 h-5" />
          )}
        </button>
      </div>

      <div className="mb-4">
        <img 
          src="https://gregciupek.com/wp-content/uploads/2025/02/grafika-n8n.png"
          alt="Supported automation systems"
          className="w-[250px] h-[40px] object-contain"
        />
      </div>

      <input
        type="url"
        value={webhookUrl}
        onChange={(e) => onWebhookChange(e.target.value)}
        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
        placeholder="https://your-webhook-url.com"
      />
      {showInfo && (
        <div className="mt-2 bg-blue-50 rounded-lg p-4 text-sm">
          <div className="flex items-center space-x-2 text-blue-700 font-medium mb-2">
            <Globe className="w-4 h-4" />
            <span>Custom Webhook Configuration</span>
          </div>

          <div className="mb-4">
            <p className="text-blue-700 font-medium mb-2">Configuration:</p>
            <ul className="list-disc list-inside space-y-1 ml-2 text-blue-600">
              <li>Method: <strong>POST</strong></li>
              <li>Mode: <strong>Respond to Webhook</strong></li>
            </ul>
          </div>

          <p className="text-blue-600 mb-2 font-medium">
            Request Format (what your webhook receives):
          </p>
          <pre className="bg-white p-3 rounded-md text-xs overflow-x-auto mb-3">
{`[
  {
    "headers": { ... },
    "params": {},
    "query": {},
    "body": {
      "test": false,
      "text": "Post content prompt",
      "photo": false,
      "modify": false,
      "is_test": false,
      "generate": true,
      "task_id": "uuid",
      "is_manual": false,
      "generate_image": true,
      "use_image_as_reference": false,
      "image_url": null,
      "platform": "facebook"
    },
    "webhookUrl": "https://your-webhook.com",
    "executionMode": "production"
  }
]`}
          </pre>

          <p className="text-blue-600 mb-2 font-medium">
            Required Response Format:
          </p>
          <pre className="bg-white p-3 rounded-md text-xs overflow-x-auto mb-3">
{`[
  {
    "success": true,
    "data": {
      "content": "Generated post content",
      "image_url": "https://cdn.example.com/image.jpg",
      "metadata": {
        "prompt": "Original prompt",
        "info": "Additional info",
        "photo_prompt": "Image generation prompt",
        "model": "AI model name",
        "processing_time": 20,
        "tokens_used": 1250
      }
    }
  }
]`}
          </pre>

          <div className="space-y-1 text-blue-700">
            <p className="font-medium">Important Notes:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Must use POST method with "Respond to Webhook" mode</li>
              <li>Response must be an array with exactly this structure</li>
              <li>Set <code className="bg-white px-1 rounded">success: true</code> on successful generation</li>
              <li>Both <code className="bg-white px-1 rounded">content</code> and <code className="bg-white px-1 rounded">image_url</code> are required</li>
              <li>The <code className="bg-white px-1 rounded">metadata</code> object is recommended for tracking</li>
              <li>If webhook fails or times out, default system webhook will be used</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};